import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, type LucideIcon } from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — stat card.
//
// The single most reused surface in the app. Every dashboard
// widget that shows "one number that matters" uses this.
//
// Responsibilities, in order:
//
//   1. Render a label, a value, and an optional delta.
//   2. Render an optional leading icon in a tinted square.
//   3. Render an optional sparkline (drawn as inline SVG, no
//      charting library, so it inherits the app's colors and
//      never introduces its own visual language).
//   4. Animate its own entrance with a small upward drift and
//      a staggered delay, so a grid of StatCards assembles
//      rather than appearing all at once.
//
// It does NOT fetch data, does NOT read from the store, and
// does NOT decide its own colors. Those are all passed in.
// ─────────────────────────────────────────────────────────────────

export type StatTone = "blue" | "green" | "orange" | "red" | "purple" | "brand";

export type StatCardProps = {
  label: string;
  value: string;
  /** Optional secondary line, e.g. "12 this term". */
  caption?: string;
  /** Percent change. Positive is up, negative is down, 0 or undefined hides the delta. */
  delta?: number;
  /** Icon rendered in a tinted square to the left of the label. */
  icon?: LucideIcon;
  /** Color family for the icon tint and the sparkline stroke. */
  tone?: StatTone;
  /** Up to ~14 numbers. Renders a sparkline if present. */
  spark?: number[];
  /** Stagger index. Multiplied by 60ms in the entrance animation. */
  index?: number;
};

// ─── Tone map ───────────────────────────────────────────────────
//
// One place where a semantic tone ("green") maps to the actual
// hex values used for the icon tint and the sparkline stroke.
// Matches Apple's system colors from tailwind.config.js — kept
// as literals here rather than Tailwind classes because we need
// the raw hex for the inline SVG gradient stops.

const TONES: Record<StatTone, { fg: string; bg: string }> = {
  blue:   { fg: "#007AFF", bg: "rgba(0, 122, 255, 0.10)" },
  green:  { fg: "#34C759", bg: "rgba(52, 199, 89, 0.12)" },
  orange: { fg: "#FF9500", bg: "rgba(255, 149, 0, 0.12)" },
  red:    { fg: "#FF3B30", bg: "rgba(255, 59, 48, 0.12)" },
  purple: { fg: "#AF52DE", bg: "rgba(175, 82, 222, 0.12)" },
  // `brand` reads from the CSS custom property set by App.tsx,
  // so it repaints when a director changes the school color
  // without this component re-rendering.
  brand:  { fg: "var(--brand-navy)", bg: "color-mix(in srgb, var(--brand-navy) 10%, transparent)" },
};

// ─── Sparkline ──────────────────────────────────────────────────
//
// A 100×32 SVG with a 2px stroke and a soft gradient fill under
// it. No axis, no grid, no labels — the sparkline is context,
// not a chart. Every charting library on earth will fight you
// on that and lose; drawing it by hand is 25 lines and gives
// us pixel control.

function Sparkline({ data, stroke }: { data: number[]; stroke: string }) {
  if (data.length < 2) return null;

  const W = 100;
  const H = 32;
  const PAD = 2;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const stepX = (W - PAD * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return [x, y] as const;
  });

  const line = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const area = `${line} L${(W - PAD).toFixed(2)},${H} L${PAD},${H} Z`;

  const gradId = `spark-${Math.random().toString(36).slice(2, 9)}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="h-8 w-full"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Delta pill ─────────────────────────────────────────────────
//
// The "▲ 4.2%" chip next to the value. Green when up, red when
// down, hidden when delta is zero or undefined. Uses a tinted
// background (12% of the accent) rather than a solid — solid
// chips read as buttons and this is not interactive.

function Delta({ value }: { value: number }) {
  if (!value) return null;
  const up = value > 0;
  const fg = up ? "#248A3D" : "#C93400";
  const bg = up ? "rgba(52, 199, 89, 0.14)" : "rgba(255, 59, 48, 0.14)";
  const Icon = up ? TrendingUp : TrendingDown;

  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-ios-pill px-1.5 py-0.5 text-ios-caption-2 font-semibold"
      style={{ color: fg, backgroundColor: bg }}
    >
      <Icon size={11} strokeWidth={2.6} absoluteStrokeWidth />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

// ─── Card ───────────────────────────────────────────────────────

export default function StatCard({
  label,
  value,
  caption,
  delta,
  icon: Icon,
  tone = "blue",
  spark,
  index = 0,
}: StatCardProps) {
  const t = TONES[tone];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.32,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: index * 0.06,
        },
      }}
      className="rounded-ios-lg bg-ios-card p-4 shadow-ios"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {Icon && (
            <span
              className="grid h-9 w-9 shrink-0 place-items-center rounded-ios-md"
              style={{ backgroundColor: t.bg, color: t.fg }}
            >
              <Icon size={17} strokeWidth={2} absoluteStrokeWidth />
            </span>
          )}
          <span className="truncate text-ios-footnote font-medium text-ios-secondary">
            {label}
          </span>
        </div>
        {delta !== undefined && <Delta value={delta} />}
      </div>

      <div className="mt-3">
        <div className="font-tabular text-ios-title-1 font-bold text-ios-label">
          {value}
        </div>
        {caption && (
          <div className="mt-0.5 text-ios-caption-1 text-ios-secondary">
            {caption}
          </div>
        )}
      </div>

      {spark && spark.length > 1 && (
        <div className="mt-3 -mx-1">
          <Sparkline data={spark} stroke={t.fg} />
        </div>
      )}
    </motion.div>
  );
}